/**
 * Frontend route definitions for the web dashboard
 */
import { defineRoute } from './route-builder.js';
/**
 * All frontend routes for the web application.
 *
 * Usage:
 *   import { webRoutes } from '@runhuman/shared';
 *   navigate(webRoutes.playground.build({ projectId: 'abc' }));
 *   // => '/dashboard/abc/playground'
 */
export const webRoutes = {
    // ============================================
    // Non-project routes (no projectId required)
    // ============================================
    /** Dashboard home page */
    dashboard: defineRoute('/dashboard'),
    /** Subscription management page */
    managePlan: defineRoute('/dashboard/manage-plan'),
    /** Plan selection / upgrade page */
    plans: defineRoute('/dashboard/plans'),
    /** Enterprise plan selection page */
    enterprisePlans: defineRoute('/dashboard/plans/enterprise'),
    /** Simple job view (redirects to full URL with projectId) */
    jobSimple: defineRoute('/dashboard/jobs/:jobId'),
    // ============================================
    // Admin routes (@volter.ai only)
    // ============================================
    /** Admin panel home (redirects to default tab) */
    admin: defineRoute('/admin'),
    /** Admin job management */
    adminJobs: defineRoute('/admin/jobs'),
    /** Admin deep-link to a specific job (bypasses org membership checks) */
    adminJobDetail: defineRoute('/admin/jobs/:jobId'),
    /**
     * Dedicated full-screen admin Job Details page (#5349) — the job details as
     * a standalone page (no jobs list behind it), opened in a new tab from the
     * Job Details drawer. Back returns to {@link adminJobs}.
     */
    adminJobPage: defineRoute('/admin/job/:jobId'),
    /** Admin tester approvals */
    adminTesters: defineRoute('/admin/testers'),
    /**
     * Admin tester profile (#4257) — full-page detail view for a single tester,
     * a richer surface than the `InternalTesterProfileDialog`. The tester ID is
     * carried as a `?tester=` query param so admins can deep-link, share, and
     * page-swap without breaking the URL contract used elsewhere
     * (see `adminTesterSummary` and `AdminTesterSummaryPage` for the same
     * pattern).
     */
    adminTesterProfile: defineRoute('/admin/tester-profile'),
    /** Admin activity log */
    adminActivity: defineRoute('/admin/activity'),
    /**
     * Admin Tester Overview (#4460, promoted in #4520) — table-driven cross-tester
     * view. One row per tester with ≥1 claimed job in the selected
     * `TesterStatsTimeRange` window, with per-row admin status actions in
     * the kebab. Surfaced as "Overview" — the first entry under the
     * "Tester" admin nav group. (Path stayed `/admin/tester-summary` when
     * the legacy master/detail page at the same slot was retired in #4520.)
     */
    adminTesterSummary: defineRoute('/admin/tester-summary'),
    /**
     * Admin Home (#3649, renamed in #3728) — read-only mirror of /tester/home.
     * Same Queue / In-Progress tabs + presence list, sourced from the shared
     * `JobsTabs` cluster in `mode='admin'`. Surfaced as "Home" under the
     * "Dashboard" admin nav group and used as the default `/admin` landing
     * page. Adds the Active tab only when the signed-in admin is also a
     * tester *and* currently has a running job.
     */
    adminHome: defineRoute('/admin/home'),
    /**
     * Home-scoped admin deep-link to a Job Details modal (#4185). Twin of
     * `adminJobDetail` (`/admin/jobs/:jobId`) but anchored to Admin Home so
     * opening a job from `/admin/home` doesn't relocate the user to the All
     * Jobs page on close. Same fetch path as `adminJobDetail` — admin-bypass
     * via `GET /api/admin/jobs/:jobId` — only the URL anchor differs.
     */
    adminHomeJob: defineRoute('/admin/home/:jobId'),
    /** Admin tester schedules */
    adminSchedules: defineRoute('/admin/schedules'),
    /** Admin Shift Reconciliations queue (#5764) — under the Operations nav group. */
    adminShiftReconciliations: defineRoute('/admin/shift-reconciliations'),
    /**
     * Admin Issue De-duplication (#6100) — one screen to scan a customer's GitHub
     * repo, Jira project, or Linear team for likely-duplicate Runhuman-filed
     * issues left by the pre-#6043 detector and clean up redundant candidates.
     * Replaces the former per-tracker duplicate tabs. Under the Operations nav group.
     */
    adminIssueDeduplication: defineRoute('/admin/issue-deduplication'),
    /**
     * Cross-org admin browse of upcoming scheduled job runs (#3784). Read-only
     * table, defaults to today → +7 days. Lives under the Dashboard admin nav
     * group alongside "All Jobs" and "Activity".
     */
    adminScheduledJobs: defineRoute('/admin/scheduled-jobs'),
    /**
     * Day-ahead ops digest admin page (#3577, #3618, #4266) — settings (Slack
     * channel + UTC hour + CC mentions) and a live preview of the upcoming
     * Slack post, surfaced under the "Tester" admin nav group. Promoted from a
     * sub-tab of `/admin/schedules` so it has a stable deep-link.
     */
    adminOpsDigest: defineRoute('/admin/ops-digest'),
    /**
     * Admin Jobs Overview (#4508) — platform-wide job analytics page: top-N
     * organizations by job count, stacked hour-of-day, terminal-state pie,
     * and a daily activity series. Sits between Ops Digest and All Jobs in
     * the Dashboard nav group.
     */
    adminJobsOverview: defineRoute('/admin/jobs-overview'),
    /**
     * Admin Tester Reviews (#4564) — fairness-driven semi-daily queue of one
     * job per active tester with a per-row 1-5 rating + report dialog. Two
     * tabs: "Queue" (the shared snapshot) and "My Reviews" (the signed-in
     * admin's authored reviews, editable). Surfaced under the "Tester"
     * admin nav group.
     */
    adminTesterReviews: defineRoute('/admin/tester-reviews'),
    /**
     * Tester Review popout (#4564) — opens in a separate browser window via
     * window.open(). Standalone form (1-5 rating + report textarea + sticky
     * Submit/Cancel) keyed to a single job; replaces the in-page dialog so
     * the review form has unlimited room to grow without crowding the queue
     * table behind it.
     */
    adminTesterReviewPopout: defineRoute('/admin/tester-reviews/popout/:jobId'),
    /**
     * Tester-side Review popout (#4564) — opens in a separate browser
     * window via window.open() from the Reviews tab dialog. Standalone
     * read-only view of one review (rating + report + date); the tester
     * uses it to keep a review pinned alongside other tabs without the
     * dialog modal blocking the rest of the portal.
     */
    testerReviewPopout: defineRoute('/tester/reviews/popout/:reviewId'),
    /** Admin organizations directory — token balances + org metadata (Polar IDs, etc.). Surfaced as "Organizations" under the "Directory" admin nav group. */
    adminOrganizations: defineRoute('/admin/organizations'),
    /**
     * Admin organization profile — full-page detail view for a single
     * organization, the richer counterpart to the `AdminOrgDetailsDialog`
     * glance. The organization ID is carried as an `?org=` query param so
     * admins can deep-link, share, and page-swap without breaking the URL
     * contract — the same pattern `adminTesterProfile` uses for `?tester=`.
     */
    adminOrganizationProfile: defineRoute('/admin/organization-profile'),
    /** Admin user management */
    adminUsers: defineRoute('/admin/users'),
    /** Admin view of the testing knowledge base (mirrors `/tester/notes`, surfaced under the "Directory" admin nav group). */
    adminNotes: defineRoute('/admin/notes'),
    /** Admin feedback submissions */
    adminFeedback: defineRoute('/admin/feedback'),
    /** Admin short links */
    adminShortLinks: defineRoute('/admin/short-links'),
    /** Admin posts */
    adminPosts: defineRoute('/admin/posts'),
    // ============================================
    // Organizations routes
    // ============================================
    /** Organizations list page */
    organizations: defineRoute('/dashboard/organizations'),
    /** Organization dashboard */
    organization: defineRoute('/dashboard/organizations/:organizationId'),
    /** Organization members page */
    organizationMembers: defineRoute('/dashboard/organizations/:organizationId/members'),
    /** Organization settings page */
    organizationSettings: defineRoute('/dashboard/organizations/:organizationId/settings'),
    /** Organization usage/billing page */
    organizationUsage: defineRoute('/dashboard/organizations/:organizationId/usage'),
    /** Organization projects page */
    organizationProjects: defineRoute('/dashboard/organizations/:organizationId/projects'),
    /** Organization jobs page (jobs across all projects) */
    organizationJobs: defineRoute('/dashboard/organizations/:organizationId/jobs'),
    /** Deep-link to a specific job inside the organization jobs list */
    organizationJob: defineRoute('/dashboard/organizations/:organizationId/jobs/:jobId'),
    /** Organization API keys page */
    organizationApiKeys: defineRoute('/dashboard/organizations/:organizationId/api-keys'),
    /** Organization integrations page */
    organizationIntegrations: defineRoute('/dashboard/organizations/:organizationId/integrations'),
    /** Organization GitHub integrations page */
    organizationGitHub: defineRoute('/dashboard/organizations/:organizationId/github'),
    /** Organization SSO/SAML configuration page */
    organizationSso: defineRoute('/dashboard/organizations/:organizationId/sso'),
    /** Organization Slack integration page */
    organizationSlack: defineRoute('/dashboard/organizations/:organizationId/slack'),
    /** Organization Sentry integration page */
    organizationSentry: defineRoute('/dashboard/organizations/:organizationId/sentry'),
    /** Organization Jira integration page */
    organizationJira: defineRoute('/dashboard/organizations/:organizationId/jira'),
    /** Organization Linear integration page */
    organizationLinear: defineRoute('/dashboard/organizations/:organizationId/linear'),
    /** Organization playground (with project selector) */
    organizationPlayground: defineRoute('/dashboard/organizations/:organizationId/playground'),
    /** Organization templates overview (aggregated across projects) */
    organizationTemplates: defineRoute('/dashboard/organizations/:organizationId/templates'),
    /** Organization schedules overview */
    organizationSchedules: defineRoute('/dashboard/organizations/:organizationId/schedules'),
    /** Organization-wide notes (customer-facing — admins manage their org's notes) */
    organizationNotes: defineRoute('/dashboard/organizations/:organizationId/notes'),
    /** Import from GitHub page */
    organizationImportGitHub: defineRoute('/dashboard/organizations/:organizationId/import'),
    /** Agent profile/setup page (Enterprise Pro + Autonomy add-on) */
    organizationAgentProfile: defineRoute('/dashboard/organizations/:organizationId/agent-profile'),
    // ============================================
    // User settings routes
    // ============================================
    /** Settings root (redirects to account) */
    settings: defineRoute('/dashboard/settings'),
    /** Account settings page */
    settingsAccount: defineRoute('/dashboard/settings/account'),
    /** Email notification preferences */
    settingsNotifications: defineRoute('/dashboard/settings/notifications'),
    // ============================================
    // Tester portal routes
    // ============================================
    /** Tester dashboard/portal home */
    tester: defineRoute('/tester'),
    /** Tester application form */
    testerApply: defineRoute('/tester/apply'),
    /** Tester available jobs list */
    testerJobs: defineRoute('/tester/jobs'),
    /** Tester Portal home — priority-sorted queue homepage */
    testerHome: defineRoute('/tester/home'),
    /**
     * Home-scoped tester deep-link to a Job Details modal (#4185). Twin of
     * `testerMyJobDetail` (`/tester/my-jobs/:jobId`) but anchored to Tester
     * Portal Home so opening a job from `/tester/home` doesn't relocate the
     * tester to the My Jobs list on close. Same fetch path as
     * `testerMyJobDetail` — `GET /api/tester/my-jobs/:jobId`, gated to the
     * claimant — only the URL anchor differs.
     */
    testerHomeJob: defineRoute('/tester/home/:jobId'),
    /** Tester settings page */
    testerSettings: defineRoute('/tester/settings'),
    /** Tester schedule (read-only view of own schedule) */
    testerScheduleView: defineRoute('/tester/schedule'),
    /** Tester timesheet — per-day scheduled / worked / break / unpaid breakdown (#3756) */
    testerTimesheetView: defineRoute('/tester/timesheet'),
    /** Tester notes — searchable knowledge base for testing info */
    testerNotes: defineRoute('/tester/notes'),
    /** Tester handbook — onboarding docs, setup guides, demo test */
    testerHandbook: defineRoute('/tester/handbook'),
    /** Public tester intro — "What is a Runhuman Tester?" landing for prospective testers (viewable signed-out) */
    testerIntroApplying: defineRoute('/tester-intro/applying'),
    /** Tester completed job history */
    testerMyJobs: defineRoute('/tester/my-jobs'),
    /** Deep-link to a specific job the tester participated in (rich details view) */
    testerMyJobDetail: defineRoute('/tester/my-jobs/:jobId'),
    /** Tester active test session */
    testerTest: defineRoute('/tester/test/:jobId'),
    /**
     * Standalone "popout" view for any job the signed-in user can read (#3849).
     * Mounts the same `JobDetailsPopout` UI surfaced inside `TesterTestPage`
     * via `?view=details`, but without the claimed-by-me requirement so admins
     * and non-claimant testers can preview a job's instructions/notes/template
     * from the queue.
     */
    jobPopout: defineRoute('/jobs/:jobId/popout'),
    // ============================================
    // Project routes (require projectId)
    // ============================================
    /** Project root (redirects to playground) */
    project: defineRoute('/dashboard/:projectId'),
    /** Playground page for testing */
    playground: defineRoute('/dashboard/:projectId/playground'),
    /** Jobs list page */
    jobs: defineRoute('/dashboard/:projectId/jobs'),
    /** Single job detail page */
    job: defineRoute('/dashboard/:projectId/jobs/:jobId'),
    /**
     * Dedicated full-screen owner Job Details page (#5349) — the job details as
     * a standalone page (no jobs list behind it), opened in a new tab from the
     * Job Details drawer. Back returns to the originating project's job list.
     */
    jobPage: defineRoute('/dashboard/job/:jobId'),
    /** Templates page */
    templates: defineRoute('/dashboard/:projectId/templates'),
    /** Single template detail (modal) */
    template: defineRoute('/dashboard/:projectId/templates/:templateId'),
    /** GitHub issues page */
    issues: defineRoute('/dashboard/:projectId/issues'),
    /** Single issue detail (modal) */
    issue: defineRoute('/dashboard/:projectId/issues/:issueNumber'),
    /** Single Jira issue detail (modal) — Jira keys have a distinct shape (PROJ-123) so we use a dedicated route */
    issueJira: defineRoute('/dashboard/:projectId/issues/jira/:issueKey'),
    /** Single Linear issue detail (modal) — Linear identifiers have a distinct shape (ENG-123) so we use a dedicated route */
    issueLinear: defineRoute('/dashboard/:projectId/issues/linear/:issueIdentifier'),
    /** Issue test sessions page */
    issueSessions: defineRoute('/dashboard/:projectId/issue-sessions'),
    /** Single test session detail (modal) */
    issueSession: defineRoute('/dashboard/:projectId/issue-sessions/:sessionId'),
    /** Project settings page */
    projectSettings: defineRoute('/dashboard/:projectId/settings'),
    /** Project integrations page (GitHub, Jira, Sentry — mirrors organizationIntegrations) */
    projectIntegrations: defineRoute('/dashboard/:projectId/integrations'),
    /** Schedules page */
    schedules: defineRoute('/dashboard/:projectId/schedules'),
    /** Project-scoped notes (customer-facing — admins manage notes for this project) */
    notes: defineRoute('/dashboard/:projectId/notes'),
    // ============================================
    // External/public routes
    // ============================================
    /** Organization invite acceptance page */
    invite: defineRoute('/invite/:token'),
    /** Clerk invitation landing page — exchanges ticket for session, then redirects to the org */
    inviteAccepted: defineRoute('/invite-accepted'),
    /** Try-Runhuman sampling flow (pre-auth) */
    try: defineRoute('/try'),
    /** Public job view with token-secured access */
    publicJob: defineRoute('/j/:jobId/:token'),
    /** Public system status page */
    statuspage: defineRoute('/statuspage'),
    /** One-click email unsubscribe confirmation (token passed as `?token=`) */
    unsubscribe: defineRoute('/unsubscribe'),
    /** Documentation */
    docs: defineRoute('/docs/setup'),
    /** Pricing page */
    pricing: defineRoute('/pricing'),
    /** Home page */
    home: defineRoute('/'),
    /** Public updates page */
    updates: defineRoute('/updates'),
    /** Single update detail page */
    updateDetail: defineRoute('/updates/:postId'),
    /** Admin email-test send harness */
    adminNotifications: defineRoute('/admin/notifications'),
    // ============================================
    // Learn Platform routes
    // ============================================
    /** Learn catalogue (default product) */
    learn: defineRoute('/learn'),
    /** Single lesson view */
    learnLesson: defineRoute('/learn/:lessonSlug'),
};
//# sourceMappingURL=web-routes.js.map